import type { AppDispatch } from "../../../common/state/store";
import { getConnection } from "./adbConnection";
import { shellCmd } from "./adbShell";
import {
  setInstallJob,
  updateInstallProgress,
  updateInstallStatus,
  updateInstallTotalSize,
} from "../state/deviceInstallerSlice";
import { TMP_DIR, CANCELLED_SENTINEL, VERIFICATION_FAILURE_SENTINEL } from "../constants";
import { downloadWithProgress, pushFileToDevice, isAbortError } from "./adbHelpers";
import { extractApkFromZip } from "../../builds/utils/zipExtractor";

/** Shared AbortController for the current install — allows cancellation from UI */
let currentAbortController: AbortController | null = null;

export function cancelCurrentInstall(): void {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

export async function installApkFromUrl(
  s3Url: string,
  originalFileName: string,
  buildId: number | undefined,
  dispatch: AppDispatch,
  packageName?: string,
): Promise<void> {
  let fileName = originalFileName;
  const adb = getConnection();
  if (!adb) {
    throw new Error("No ADB connection");
  }

  const abortController = new AbortController();
  currentAbortController = abortController;
  const { signal } = abortController;

  // Step 1: Downloading
  dispatch(
    setInstallJob({
      buildId,
      fileName,
      packageName,
      status: "downloading",
      progress: 0,
    }),
  );

  try {
    const { data: downloadedData, totalSize } = await downloadWithProgress(
      s3Url,
      signal,
      (percent) => dispatch(updateInstallProgress(percent)),
    );
    if (totalSize > 0) {
      dispatch(updateInstallTotalSize(Math.round((totalSize / 1024 / 1024) * 10) / 10));
    }

    let apkData = downloadedData;

    // Verify ZIP/APK header (PK\x03\x04)
    if (apkData[0] !== 0x50 || apkData[1] !== 0x4b) {
      throw new Error(
        `Invalid file (got ${apkData.length} bytes, header: ${apkData[0]?.toString(
          16,
        )}${apkData[1]?.toString(16)}). The URL may have returned an error page.`,
      );
    }

    if (signal.aborted) throw new DOMException("Installation cancelled", "AbortError");

    // Step 2: Extracting
    dispatch(updateInstallStatus({ status: "extracting" }));
    dispatch(updateInstallProgress(0));

    try {
      const extracted = await extractApkFromZip(apkData);
      apkData = extracted.apkData;
      fileName = extracted.apkName.split("/").pop() || fileName;
      // Update total size to reflect extracted APK size
      dispatch(updateInstallTotalSize(Math.round((apkData.byteLength / 1024 / 1024) * 10) / 10));
    } catch (extractErr: unknown) {
      const msg = extractErr instanceof Error ? extractErr.message : String(extractErr);
      // "No .apk file found" or "Invalid ZIP" → treat the raw file as a standalone APK.
      // Any other error (decompression failure, corrupt entry) is a real problem — rethrow.
      if (!msg.includes("No .apk file found") && !msg.includes("Invalid ZIP")) {
        throw new Error(`ZIP extraction failed: ${msg}`);
      }
    }

    dispatch(updateInstallProgress(100));

    if (signal.aborted) throw new DOMException("Installation cancelled", "AbortError");

    // Re-check connection before device I/O — the device may have been
    // unplugged during the (potentially long) download + extraction.
    const adbBeforePush = getConnection();
    if (!adbBeforePush) {
      throw new Error("Device disconnected during download");
    }

    // Step 3: Pushing to device
    dispatch(updateInstallStatus({ status: "pushing" }));
    dispatch(updateInstallProgress(0));

    const remotePath = `${TMP_DIR}/${fileName}`;

    await pushFileToDevice(adbBeforePush, remotePath, apkData, undefined, (percent) =>
      dispatch(updateInstallProgress(percent)),
    );

    dispatch(updateInstallProgress(100));

    if (signal.aborted) throw new DOMException("Installation cancelled", "AbortError");

    // Re-check connection before pm install
    const adbBeforeInstall = getConnection();
    if (!adbBeforeInstall) {
      throw new Error("Device disconnected during file transfer");
    }

    // Step 4: Installing
    dispatch(updateInstallStatus({ status: "installing" }));

    const output = await shellCmd(adbBeforeInstall, ["pm", "install", "-r", remotePath]);

    // Clean up temp file
    try {
      await shellCmd(adbBeforeInstall, ["rm", "-f", remotePath]);
    } catch (err) {
      console.warn(`[ADB] Temp file cleanup failed for ${remotePath}:`, err);
    }

    // Check result
    if (output.includes("Success")) {
      dispatch(updateInstallStatus({ status: "success" }));

      // Rescan installed packages so the UI updates (e.g. "Installed" badge)
      try {
        const { scanInstalledPackages } = await import("./packageScanner");
        await scanInstalledPackages(dispatch);
      } catch {
        // Non-blocking
      }
    } else {
      let errorMessage = output.trim() || "Unknown installation error";
      if (output.includes("INSTALL_FAILED_VERIFICATION_FAILURE")) {
        errorMessage = VERIFICATION_FAILURE_SENTINEL;
      }
      dispatch(
        updateInstallStatus({
          status: "error",
          errorMessage,
        }),
      );
    }
  } catch (err: unknown) {
    if (isAbortError(err)) {
      dispatch(updateInstallStatus({ status: "error", errorMessage: CANCELLED_SENTINEL }));
    } else {
      const message = err instanceof Error ? err.message : String(err);
      dispatch(
        updateInstallStatus({
          status: "error",
          errorMessage: message,
        }),
      );
    }
  } finally {
    currentAbortController = null;
  }
}
